/**
 * Manages RPA-trigger state and the document upload handler:
 * loading, button-disabled logic, hide-button memo, and `handleSaveChanges`.
 * @module useDocumentRpaActions
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  uploadAuthLetter,
  UploadAuthLetterPayload,
} from "../../../../../api/postCall/uploadAuthLetter";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { authStatusForDisablingTriggerRpa } from "../../../../../constants/authStatusForDisablingTriggerRpa";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { User } from "../../../../../data-model/firebaseAuthModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
  JCodeDetails,
  JCodeDocuments,
} from "../../../../../data-model/medicalPaOrdersModel";
import { DocWriteBackStatus } from "../../../../../enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { getDocTypeFromOrgStrict } from "../../../../../utils/getDocFromOrg";
import {
  formatJCodesForTrailComment,
  insertDocumentUploadedToOncoemrTrailComment,
  resolveJCodeLabelsForSelectedDocuments,
} from "../../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";

/**
 * Inputs for {@link useDocumentRpaActions}: Postgres-backed order/auth slices,
 * flattened jcodes, current user, payer label, facility auth-letter type, and Redux dispatch.
 */
interface UseDocumentRpaActionsParams {
  unwrappedGeneralOrderDetails: GeneralOrderDetails | null;
  unwrappedAuthOnFileDetails: AuthOnFile | null;
  generalOrderDetails: { order: GeneralOrderDetails } | null;
  jcodes: JCodeDetails[] | undefined;
  user: User | null;
  payerName: string;
  healthCareFacilityData: {
    data?: { auth_letter_type?: string };
    loading: boolean;
  } | null;
  dispatch: AppDispatch;
}

/**
 * Encapsulates all RPA-trigger concerns: debounced save handler,
 * button-disabled state derived from order status, and a memo
 * that hides the button for certain auth statuses.
 *
 * @param params - Order data, user info, and dispatch needed by the handler.
 * @returns `loading`, `rpaButtonDisabled`, `shouldHideTriggerRpaButton`, and `handleSaveChanges`.
 */
export const useDocumentRpaActions = ({
  unwrappedGeneralOrderDetails,
  unwrappedAuthOnFileDetails,
  generalOrderDetails,
  jcodes,
  user,
  payerName,
  healthCareFacilityData,
  dispatch,
}: UseDocumentRpaActionsParams) => {
  const { fetchAll, authOnFileDetails } = useDataAccumulatorForSingleOrder();
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const [loading, setLoading] = useState(false);
  const lastExecutionTimeRef = useRef<number>(0);
  const authOnFileStatus = authOnFileDetails?.auth_on_file?.auth_status;
  const authLetterType = authStatusOptions?.find(
    (option) => option.id === authOnFileStatus,
  )?.auth_letter_type;

  /**
   * Resolved document type used as the `document_type` for the RPA upload
   * payload. Precedence (first truthy wins):
   *  1. `authLetterType` mapped from the current auth status option.
   *  2. `healthCareFacilityData.data.auth_letter_type` configured per facility.
   *  3. Org-default via `getDocTypeFromOrgStrict(orgId)`.
   * Resolves to `string | undefined`; `getDocTypeFromOrgStrict` itself falls
   * back to `"Authorization / Referrals"` for unmapped orgs.
   */
  const computedAuthLetterType =
    authLetterType ||
    healthCareFacilityData?.data?.auth_letter_type ||
    getDocTypeFromOrgStrict(getOrgIdForFetchExternalWorklist());
  // Individual RPA blockers, exposed so the header can show a specific
  // tooltip message per failing condition.
  const rpaBlockers = useMemo(() => {
    const documentUploadStatus =
      generalOrderDetails?.order?.status?.document_upload_status;
    return {
      isRpaInProgress: loading,
      isDocumentWriteBackInProgress:
        documentUploadStatus === DocWriteBackStatus.IN_PROGRESS ||
        documentUploadStatus === DocWriteBackStatus.QUEUED,
      hasNoJCodes: !jcodes || jcodes.length === 0,
    };
  }, [loading, generalOrderDetails, jcodes]);

  const rpaButtonDisabled = useMemo(
    () => Object.values(rpaBlockers).some(Boolean),
    [rpaBlockers],
  );

  const shouldHideTriggerRpaButton = useMemo(() => {
    return authStatusForDisablingTriggerRpa.includes(
      unwrappedAuthOnFileDetails?.auth_status ?? "",
    );
  }, [unwrappedAuthOnFileDetails?.auth_status]);

  /**
   * Uploads selected documents via RPA for the parent order, optionally
   * including linked sibling order ids in the same request payload.
   *
   * @param selectedDocuments - Documents checked in the modal
   * @param selectedOrderIds - Optional linked sibling order ids from the modal
   */
  const handleSaveChanges = async (
    selectedDocuments: JCodeDocuments[],
    selectedOrderIds?: string[],
  ) => {
    const currentTime = Date.now();
    const timeSinceLastExecution = currentTime - lastExecutionTimeRef.current;
    if (timeSinceLastExecution < 10000) {
      toast.error(ToastMessages.RETRY_ERROR);
      return;
    }

    lastExecutionTimeRef.current = currentTime;

    const extractFlattenDocsWithUniqueDocRefsPath = selectedDocuments
      .map((doc) => ({
        document_type: computedAuthLetterType,
        document_name: doc?.doc_title ?? "",
        document_path: doc?.doc_path ?? "",
      }))
      .filter(
        (doc, index, self) =>
          index ===
          self.findIndex((t) => t.document_path === doc.document_path),
      );

    const parentOrderId = unwrappedGeneralOrderDetails?.order_id ?? "";
    const patientId =
      unwrappedGeneralOrderDetails?.demographics?.patient_id ??
      unwrappedGeneralOrderDetails?.demographics?.patient_mrn ??
      "";

    const siblingOrderIds =
      selectedOrderIds?.filter(
        (linkedOrderId) => linkedOrderId && linkedOrderId !== parentOrderId,
      ) ?? [];

    const payloadForRpa: UploadAuthLetterPayload = {
      order_id: parentOrderId,
      patient_id: patientId,
      documents: extractFlattenDocsWithUniqueDocRefsPath,
      org_id: getOrgIdForFetchExternalWorklist() || "",
      ...(siblingOrderIds.length > 0
        ? { sibling_order_ids: [...new Set(siblingOrderIds)] }
        : {}),
    };

    try {
      apiWorkerManager.enqueueAndProcess({
        patient_id: patientId,
        order_id: parentOrderId,
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.DOCUMENT_UPLOAD_INITIATED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          payer_name: payerName,
        },
      });
      setLoading(true);
      const response = await uploadAuthLetter(payloadForRpa);

      if (!response?.success) {
        toast.error(ToastMessages.DOCUMENTS_UPDATED_ERROR);
        return;
      }

      const orgId = getOrgIdForFetchExternalWorklist();
      const orderId = parentOrderId;
      const actingProviderName =
        `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
        "Unknown Provider";
      const uploadedDocumentNames = formatJCodesForTrailComment([
        ...new Set(
          extractFlattenDocsWithUniqueDocRefsPath
            .map((doc) => doc.document_name)
            .filter(Boolean),
        ),
      ]);

      insertDocumentUploadedToOncoemrTrailComment({
        orderId,
        orgId: orgId ?? "",
        patientId: unwrappedGeneralOrderDetails?.demographics?.patient_id ?? "",
        providerEmail: user?.email ?? "",
        providerName: actingProviderName,
        documentLabel: uploadedDocumentNames || computedAuthLetterType,
        jCodeLabels: resolveJCodeLabelsForSelectedDocuments(
          selectedDocuments,
          jcodes,
        ),
      });

      if (orderId && orgId) {
        await fetchAll(orderId, orgId);
      }
      dispatch(closeModal());
      toast.success(ToastMessages.DOCUMENTS_UPDATED_SUCCESS);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    rpaButtonDisabled,
    rpaBlockers,
    shouldHideTriggerRpaButton,
    handleSaveChanges,
  };
};
